import React, { useState, useEffect, useRef } from 'react';
import { FormControl, Dropdown } from 'react-bootstrap';
import "./index.css";

const SearchableDropdown = ({
  options = [],
  onSelect,
  onInputChnge,
  placeholder = "Search...",
  defaultSelected = null,
  selectedValue,
  disabled = false,
  loading = false,
  isClearable = false,
  isSearchable = true,
  iconSize = '18px'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedItem, setSelectedItem] = useState(defaultSelected);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const isInternalChange = useRef(false);
  const inputRef = useRef(null);

  // Effect to sync with external selectedValue
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    if (selectedValue) {
      const label = typeof selectedValue === 'string' ? selectedValue : selectedValue?.label;
      const matched = options.find(opt => opt.label === label);
      if (matched) {
        setSelectedItem(matched);
        setSearchTerm('');
      } else {
        setSelectedItem(null);
      }
    } else {
      setSelectedItem(null);
      setSearchTerm('');
    }
  }, [selectedValue, options]);

  // Effect to filter options
  useEffect(() => {
    if (loading) {
      setFilteredOptions([]);
      return;
    }

    if (!showDropdown) {
      setFilteredOptions(options);
      return;
    }

    if (isSearchable && searchTerm && searchTerm.trim() !== '') {
      setFilteredOptions(
        options.filter(option =>
          option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options, isSearchable, loading, showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    isInternalChange.current = true;
    setSelectedItem(option);
    setSearchTerm('');
    onSelect(option);
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (!showDropdown) {
      setShowDropdown(true);
    }
    
    if (onInputChnge) onInputChnge(value);
    
    if (!value && !selectedItem) {
      onSelect(null);
    }
  };

  const handleClear = () => {
    isInternalChange.current = true;
    setSearchTerm('');
    setSelectedItem(null);
    onSelect(null);
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      const newState = !showDropdown;
      setShowDropdown(newState);
      
      if (newState) {
        setSearchTerm('');
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    }
  };

  const showLoadingInMenu = loading && showDropdown;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <Dropdown show={showDropdown} style={{ width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <FormControl
            ref={inputRef}
            disabled={disabled}
            type="text"
            placeholder={selectedItem ? selectedItem.label : placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onClick={handleInputClick}
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            className="form-select-value"
            style={{ 
              paddingRight: isClearable && selectedItem ? '60px' : '30px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              backgroundColor: '#ffffff !important',
              color: '#212529',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              height: '38px'
            }}
          />

          {isClearable && selectedItem && !disabled && !loading && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              style={{
                position: 'absolute',
                right: '28px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: '#6c757d',
                cursor: 'pointer',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: iconSize,
                height: iconSize
              }}
            >
              <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <Dropdown.Menu
          style={{
            width: '100%',
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '2px',
            padding: '4px 0',
            backgroundColor: '#ffffff',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          className="py-0 dropdown-equipment-wp"
        >
          {showLoadingInMenu ? (
            <Dropdown.Item disabled className="items-lable-value text-center">
              <span>Loading...</span>
            </Dropdown.Item>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = selectedItem && (selectedItem.value === option.value || selectedItem.id === option.id);
              return (
                <Dropdown.Item
                  key={option.value || option.id || option.label}
                  onClick={() => handleSelect(option)}
                  className="items-lable-value"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e9ecef' : 'transparent',
                    color: '#212529',
                    transition: 'background-color 0.15s ease',
                    border: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {option.label}
                </Dropdown.Item>
              );
            })
          ) : (
            <Dropdown.Item disabled className="items-lable-value" style={{ padding: '8px 12px' }}>
              {loading ? 'Loading...' : searchTerm ? 'No options found' : 'No options available'}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default SearchableDropdown;